import React from "react";
import "./Results.css";
import emptyImage from "../images/c.png";

const formatKm = (km) => `${km.toFixed(1)} km`;

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours ? `${hours} h ${mins} m` : `${mins} m`;
};

const roleFor = (index, length) => {
  if (index === 0) return "Origin";
  if (index === length - 1) return "Destination";
  return `Stop ${index}`;
};

const Results = ({ result, isLoading }) => (
  <section className="results section" id="results">
    <div className="container">
      <div className="results__head">
        <span className="eyebrow">Your route</span>
        <h2>The shortest way round.</h2>
      </div>

      {isLoading && (
        <div className="results__card results__card--loading">
          <div className="results__skeleton results__skeleton--stats" />
          {[0, 1, 2, 3].map((i) => (
            <div className="results__skeleton" key={i} />
          ))}
          <p className="results__loading-note">Untangling the route…</p>
        </div>
      )}

      {!isLoading && !result && (
        <div className="results__empty">
          <div className="results__empty-copy">
            <h3>Nothing planned yet.</h3>
            <p>
              Add an origin and a destination above, then hit{" "}
              <em>Find the shortest route</em>. The ordered stops, per-leg
              distances and a time estimate will land here.
            </p>
            <a className="btn btn--ghost" href="#planner">
              Go to the planner
            </a>
          </div>
          <img
            src={emptyImage}
            alt="Papercraft highway interchange"
            loading="lazy"
            width="1024"
            height="1024"
          />
        </div>
      )}

      {!isLoading && result && (
        <div className="results__card">
          <dl className="results__stats">
            <div>
              <dt>Total distance</dt>
              <dd>{formatKm(result.totalKm)}</dd>
            </div>
            <div>
              <dt>Estimated time</dt>
              <dd>{formatDuration(result.minutes)}</dd>
            </div>
            <div>
              <dt>Stops</dt>
              <dd>{Math.max(0, result.ordered.length - 2)}</dd>
            </div>
            <div className="results__stat--accent">
              <dt>Saved vs. your order</dt>
              <dd>{formatKm(result.savedKm)}</dd>
            </div>
          </dl>

          <ol className="results__timeline">
            {result.ordered.map((address, index) => (
              <li
                className="results__stop"
                key={`${address}-${index}`}
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <span
                  className={`results__marker${
                    index === 0
                      ? " results__marker--origin"
                      : index === result.ordered.length - 1
                      ? " results__marker--destination"
                      : ""
                  }`}
                  aria-hidden="true"
                >
                  {index === 0 || index === result.ordered.length - 1
                    ? ""
                    : index}
                </span>
                <div className="results__stop-body">
                  <span className="results__role">
                    {roleFor(index, result.ordered.length)}
                  </span>
                  <span className="results__address">{address}</span>
                </div>
                {result.legs[index] && (
                  <span className="results__leg">
                    {formatKm(result.legs[index].km)}
                  </span>
                )}
              </li>
            ))}
          </ol>

          {result.source === "local" && (
            <p className="results__note">
              <strong>Demo mode.</strong> The routing API is restricted to the
              deployed domain, so distances here are computed locally from
              synthetic coordinates. The ordering logic is real; the kilometres
              are not.
            </p>
          )}
        </div>
      )}
    </div>
  </section>
);

export default Results;
